import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { NavController, ModalController, LoadingController, PopoverController, MenuController } from '@ionic/angular';

import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { NativeGeocoder, NativeGeocoderResult } from '@ionic-native/native-geocoder/ngx';
import { Storage } from '@ionic/storage';

import { PublicEventInfo } from '../../models/public-event-info';
import { Address } from '../../models/address';
import { Constants } from "../../models/constants";
import { SelectedServiceTypeList } from "../../models/selected-service-type-list";
import { Popup } from '../../models/popup';

import { MainScreenContentParams, MainScreenContent } from '../../services/main-screen-content';
import { ApptCreator } from "../../services/appt-creation-service";
import { UserService } from "../../services/user-service";
import { Timezones } from "../../services/timezones";
import { Util } from "../../services/util";
import { DeeplinkRoutingService } from "../../services/deeplink-routing-service";
import { PermissionsService, PermissionStatus } from "../../services/permissions-service";
import { CurrentLocationService } from '../../services/current-location-service';
import { Alert } from '../../services/alert';
import { EventService } from '../../services/event-service';
import { ExternalLinkService } from '../../services/external-link-service';
import { RoomTasks } from '../../services/room-tasks';
import { AnalyticsService } from '../../services/analytics-service';
import { ActivatedRoute, NavigationExtras } from '@angular/router';

import { EnterAddressPage } from '../enter-address/enter-address.page';
import { ServicesListPage } from '../services-list/services-list.page';
import { WeekSelectorPage } from 'src/app/pages/week-selector/week-selector.page';
import { MetaTagService } from 'src/app/services/meta-tag-service';
import { ObservableService } from 'src/app/services/observable-service';
import { PopupPage } from '../popup/popup.page';

@Component({
  selector: 'app-main-screen',
  templateUrl: './main-screen.page.html',
  styleUrls: ['./main-screen.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MainScreenPage implements OnInit {

  profPicThumbURL = "assets/img/profile-placeholder.png";

  emptyStateNoLocation = false;
  errorString: string

  roundToTwo = Util.roundToTwo;

  screenItems: any[] = [];

  searchingCurrentLocation = false;

  private popupsToShow: Popup[] = []

  businessID?: string

  // Subscriptions
  private appActiveSubscription: any;
  private branchSubscription: any;
  private locationSubscription: any;
  private profPicSubscription: any;

  constructor(
    private observables: ObservableService,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private inAppBrowser: InAppBrowser,
    private deeplinkRoutingService: DeeplinkRoutingService,
    private permissionsService: PermissionsService,
    private currentLocationService: CurrentLocationService,
    private alert: Alert,
    private eventService: EventService,
    private nativeGeocoder: NativeGeocoder,
    private externalLinkService: ExternalLinkService,
    private analytics: AnalyticsService,
    private storage: Storage,
    private loadingCtrl: LoadingController,
    private changeRef: ChangeDetectorRef,
    public apptCreator: ApptCreator,
    public userService: UserService,
    private popoverCtrl: PopoverController,
    private menuCtrl: MenuController,
    private metaTagService: MetaTagService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.businessID = this.route.snapshot.queryParamMap.get('b') ?? this.deeplinkRoutingService.storedBusinessID;
    this.deeplinkRoutingService.storedBusinessID = this.businessID;
  }

  ngAfterViewInit() {

    this.observables.publishUpdateUser()

    this.permissionsService.locationPermissionStatus((status) => {
      if (status != PermissionStatus.Granted) {
        this.permissionsService.requestLocationPermission();
      }
    });

    const user = this.userService.user;

    if (!user) {
      const sub = this.observables.userFetched.subscribe(() => {
        sub.unsubscribe()
        let eventualLoad = true
        this.loadInitialContent(eventualLoad)
      });
      return
    }

    this.loadInitialContent()

  }

  ionViewWillEnter() {

    this.metaTagService.updateTitle('Search Pros')
    this.metaTagService.updateTags({
      facebookURL: 'https://app.getshortcut.co/explore',
      keywords: 'search Pros, in home haircuts, on demand haircuts, local barbers, local stylists',
      facebookTitle: 'Search Pros | Shortcut',
      twitterTitle: 'Search Pros | Shortcut',
      description: 'Find local Pros for in-home services through Shortcut.',
      facebookDescription: 'Find local Pros for in-home services through Shortcut.',
      twitterDescription: 'Find local Pros for in-home services through Shortcut.',
    })

    this.menuCtrl.enable(true);

    this.appActiveSubscription = this.observables.appBecameActive.subscribe(() => {
      this.handleDeeplinks()
    });

    this.branchSubscription = this.observables.branchLinkClicked.subscribe(() => {
      this.handleDeeplinks()
    });

    this.locationSubscription = this.observables.locationPermissionGranted.subscribe(() => {
      console.log('Calling get location from permission granted');
      this.getCurrentLocation();
    });

    this.profPicSubscription = this.observables.profPicUpdated.subscribe(() => {
      this.profPicThumbURL = this.userService.user.profPicThumbURL;
    })
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/explore')
    this.handleDeeplinks()
    this.changeRef.detectChanges()
  }

  ionViewWillLeave() {
    this.appActiveSubscription.unsubscribe()
    this.branchSubscription.unsubscribe()
    this.locationSubscription.unsubscribe()
    this.profPicSubscription.unsubscribe()
  }

  loadInitialContent(eventualLoad?: boolean) {

    this.userService.fetch(() => {
      if (this.userService.user && this.userService.user.profPicThumbURL) {
        this.profPicThumbURL = this.userService.user.profPicThumbURL;
      }

      if (this.userService.user && this.userService.user.address) {
        this.apptCreator.address = this.userService.user.address;
      }

      if (this.businessID) {
        this.pullContent();
      } else {
        this.refreshAddressField_PullContent();
      }

      if (!this.userService.user.isGuest && (!this.userService.user.email || this.userService.user.email === '')) {
        this.observables.publishGoToEmptyNameEmail()
      }

      if (eventualLoad) {
        this.changeRef.detectChanges()
      }
    }, (errorMsg) => {
      console.log(errorMsg)
    })
  }


  // Get the content

  pullContent(refresher?: any) {

    this.emptyStateNoLocation = false;

    if (this.apptCreator.apptTime.timezone || this.businessID) {
      this.executePullContent(refresher);
    } else {
      console.log("Calling timezone info for address from pull content");
      Timezones.timezoneInfoForAddress(this.apptCreator.address, this.apptCreator.apptTime.date, (timezone) => {
        this.apptCreator.apptTime.timezone = timezone;
        this.executePullContent(refresher);
      }, (errorMsg) => {
        this.emptyStateNoLocation = true;
        if (refresher) refresher.target.complete();
        this.changeRef.detectChanges()
      });
    }
  }

  executePullContent(refresher?: any) {

    this.errorString = undefined

    var params: MainScreenContentParams = {
      businessID: this.businessID,
      proType: this.userService.user.currentProType,
      showContentWhenUnavailable: true,
      shouldShowConciergeChat: true,
      shouldShowURLBanner: true,
      // startTime: this.apptCreator.mainScreenSelectedDay.date,
      weekNumber: this.apptCreator.mainScreenSelectedWeek.weekNumber,
      timezoneOffset: this.apptCreator.apptTime.timezone ? this.apptCreator.apptTime.timezone.offset : (new Date()).getTimezoneOffset(),
      serviceTypeList: this.apptCreator.mainScreenSelectedServiceTypeList.items,
      address: this.apptCreator.address,
      includePopups: true,
      includeOutOfRadiusAvails: true,
      includeOtherServiceCategories: true
    }

    if (refresher) {
      // On hard refresh, skip the backend's cache.
      params.skipCache = true
    }

    MainScreenContent.load(params,
      (content: any[], popups: Popup[]) => {
        this.loadedContent(content);

        this.storage.get(Constants.SEEN_POPUPS_LIST).then((val) => {
          let seenPopupsList = []
          if (val) seenPopupsList = val

          let unseenPopups = []
          for (const popup of popups) {
            if (!seenPopupsList.includes(popup.id)) unseenPopups.push(popup)
          }

          this.loadedPopups(unseenPopups);
        })

        if (refresher) refresher.target.complete();
      },
      (msg: string) => {
        if (refresher) refresher.target.complete();
        if (!Util.isNavigationError(msg)) {
          this.error(msg);
          this.errorString = msg
        }
      });
  }

  async switchProType(proType: string) {
    let newParams = {
      currentProType: proType
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    this.userService.update(newParams,
      () => {
        loading.dismiss()
        let list = new SelectedServiceTypeList()
        list.items = []
        list.refreshText()
        this.apptCreator.mainScreenSelectedServiceTypeList = list
        this.reset()
        this.pullContent();
        this.observables.publishRefreshSideMenu()
      },
      (msg: string) => {
        loading.dismiss()
        alert(msg)
      });
  }


  //
  // -- Helpers --
  //

  private handleDeeplinks() {
    this.observables.publishHandleDeeplinks()
  }

  openURL(url: string) {
    this.inAppBrowser.create(url, "_blank")
  }

  async joinEvent(eventID: string) {

    let loading = await this.loadingCtrl.create()
    loading.present()

    this.eventService.pullPublicEventInfo(eventID, (eventInfo: PublicEventInfo) => {

      loading.dismiss()
      let navigationExtras = {
        state: {
          publicEventInfo: eventInfo,
        }
      }
      this.navCtrl.navigateForward(`/event-details/${eventID}`, navigationExtras)

    }, (errorMsg: string) => {
      loading.dismiss()

      if (!Util.isNavigationError(errorMsg)) {
        this.alert.show("Error", errorMsg);
      }

    });
  }

  formatCost(n: number, currency: string) {
    return Util.formatCost(n, currency);
  }

  isRecognizedButton(button: any) {
    let recognizedButtonTypes = ['sign-up', 'shortcut-instagram', 'enable-notifications', 'url', 'pro-type-change-to-hair']
    return Util.contains(recognizedButtonTypes, button.type)
  }

  handleButtonGroupPressed(button: any) {

    if (button.type == 'sign-up') {
      const preventMainRefresh = false
      const source = ''
      this.observables.publishGoToSignUp(preventMainRefresh, source)
    } else if (button.type == 'enable-notifications') {
      if (Constants.BROWSER_MODE) {
        this.observables.publishShowDownloadPopup()
        return
      }
    } else if (button.type == 'shortcut-instagram') {
      this.externalLinkService.openInstagram('shortcut')
    } else if (button.type == 'url') {
      this.inAppBrowser.create(button.url, "_blank")
    } else if (button.type == 'pro-type-change-to-hair') {
      this.switchProType('Hair')
    }
  }

  private loadedContent(content: any[]) {
    var hasAvailGroups = false
    for (let item of content) {
      if (item.type == 'day-segment-group' || item.type == 'barber-group') {
        hasAvailGroups = true

        const numProsInitialLoad = 2;

        if (item.avails.length > numProsInitialLoad) {
          item.showingAvails = item.avails.slice(0, numProsInitialLoad)
        } else {
          item.showingAvails = item.avails
        }
      }
    }

    this.screenItems = content;
    this.changeRef.detectChanges()
  }

  private loadedPopups(popups: Popup[]) {
    this.popupsToShow = popups
    this.showNextPopup()
  }

  private async showNextPopup() {
    if (this.popupsToShow.length > 0) {
      const nextPopup = this.popupsToShow.shift()

      let popupModal = await this.modalCtrl.create({
        component: PopupPage,
        componentProps: {
          popup: nextPopup
        },
        cssClass: 'auto-height',
      })

      popupModal.onDidDismiss().then((val: any) => {
        this.showNextPopup()
      });

      popupModal.present();

      let seenPopupsList = []
      try {
        const val = await this.storage.get(Constants.SEEN_POPUPS_LIST)
        if (val) seenPopupsList = val
      } catch (error) {
        console.log(error)
      }
      seenPopupsList.push(nextPopup.id)
      this.storage.set(Constants.SEEN_POPUPS_LIST, seenPopupsList)
    }
  }

  showMoreAvails(item) {
    const numMoreToShow = 4;
    const lastIndex = item.showingAvails.length + numMoreToShow;

    // Note: This can go over the end w/o a problem.
    item.showingAvails = item.avails.slice(0, lastIndex);

    this.changeRef.detectChanges()
  }

  private error(msg: string) {
    console.error(msg)
  }

  private getCurrentLocation(successCallback?: () => void) {
    // Only search for current location if we don't have an address set yet.
    if (this.apptCreator.address.zip != '') {
      console.log("Returning from getCurrentLocation because we already have an address.");
      if (successCallback) {
        successCallback();
      };
      return;
    }

    if (this.searchingCurrentLocation) {
      console.log('Already searching for current location. Returning.');
      return;
    }

    this.searchingCurrentLocation = true;
    this.currentLocationService.fetchCurrentLocation((address: Address) => {
      this.analytics.track(AnalyticsService.EnteredAddress_GPS)
      this.storage.set(Constants.ADDRESS_IS_FROM_GPS, true)
      this.apptCreator.address = address;
      let fromGPS = true;
      this.saveUserAddress(fromGPS);
      successCallback();
    }, (error: string) => {
      this.searchingCurrentLocation = false;
      this.emptyStateNoLocation = true;
    });
  }

  private reset() {
    this.screenItems = [];
    this.emptyStateNoLocation = false;
  }

  private saveUserAddress(fromGPS: boolean) {

    if (fromGPS) {
      this.userService.update({
        gpsAddress: this.apptCreator.address
      }, () => {
        this.userService.fetch(() => {
          this.changeRef.detectChanges()
        })
      }, (errorMsg) => { });
      return;
    }

    this.userService.update({
      address: this.apptCreator.address
    }, () => {
      this.changeRef.detectChanges()
    }, (errorMsg) => { });
  }

  private refreshAddressField_PullContent() {

    this.emptyStateNoLocation = false;

    if (this.apptCreator.address.zip != '') {

      // TODO: Refresh coords if they are 0
      if (this.apptCreator.address.coordinates.latitude == 0 && this.apptCreator.address.coordinates.longitude == 0) {

        this.nativeGeocoder.forwardGeocode(Util.formatAddress(this.apptCreator.address))
          .then((value: NativeGeocoderResult[]) => {

            let geocoderCoordinates = value[0]
            this.apptCreator.address.coordinates = {
              latitude: Number(geocoderCoordinates.latitude),
              longitude: Number(geocoderCoordinates.longitude)
            }

            this.completePullContent()

          })
          .catch((error: any) => {
            this.emptyStateNoLocation = true;

          });

      } else {

        this.completePullContent()

      }

    } else {
      this.getCurrentLocation(() => {
        this.refreshAddressField_PullContent();
      });
    }
  }

  private completePullContent() {

    this.emptyStateNoLocation = false;

    Timezones.timezoneInfoForAddress(this.apptCreator.address, this.apptCreator.apptTime.date, (timezone) => {
      this.apptCreator.apptTime.timezone = timezone;
      this.pullContent();
    }, (errorMsg) => {
      this.emptyStateNoLocation = true;
    });

  }

  private goToChat(roomID: string) {
    this.observables.publishOpenNewChat(roomID)
  }

  photoGroupPressed(photo: any) {

    if (photo.action == 'join-event') {

      this.alert.showPrompt('Join An Event', null, 'code', 'CODE', 'text', 'Submit', (data) => {

        console.log(data.code)
        this.observables.publishEnterEventCode(data.code)

      }, 'Cancel');

    } else if (photo.action == 'open-browser') {

      this.inAppBrowser.create(photo.browserURL, "_blank");

    }
  }

  joinEventBannerPressed(eventID: string) {
    this.alert.showPrompt('This event is private.', 'Please enter the event code.', 'code', 'CODE', 'text', 'Submit', (data) => {

      this.observables.publishEnterEventCode(data.code)

    }, 'Cancel');
  }

  async conciergeChatFromBanner() {
    let loading = await this.loadingCtrl.create()
    loading.present()

    this.analytics.track(AnalyticsService.PressConciergeBanner)

    RoomTasks.createClientConciergeRoom((roomID: string) => {

      loading.dismiss();
      this.goToChat(roomID)

    }, (err: string) => {

      loading.dismiss();
      console.error(err)
      this.alert.show('Error', err);

    })
  }




  // Navigation

  getAdjustedDisplayTime(time: Date, timezoneOffset: number) {
    return Util.prettyTimeAdjusted(time, { offset: timezoneOffset, abbr: undefined })
  }

  getAdjustedDisplayDate(date: Date, timezoneOffset: number) {
    return Util.shortMonthDayNoTh(date, { offset: timezoneOffset, abbr: undefined })
  }

  stringToDate(timeString: string) {
    return new Date(timeString)
  }

  goToBarber(barberLink: string, barberProfileNavHistory: string, selectedDate?: Date, canBook?: boolean) {

    const navigationExtras: NavigationExtras = {
      state: {
        barberProfileNavHistory,
        apptDate: selectedDate,
        canBook
      }
    }
    this.navCtrl.navigateForward(`/pro/${barberLink}`, navigationExtras);
  }

  roundedRating(rating: number) {
    return Math.round(rating)
  }

  toRange(n: number) {
    return Util.toRange(n)
  }

  async openAddress(event: any) {

    const addressModal = await this.popoverCtrl.create({
      component: EnterAddressPage,
      event: event,
      componentProps: {},
      cssClass: ['SCWEB-popover', 'SCWEB-popover-address']
    })

    addressModal.onDidDismiss().then((val: any) => {
      if (val && val.data && val.data.address) {
        this.analytics.track(AnalyticsService.EnteredAddress_MainScreen)
        const fromGPS = val.data.isFromGPS;
        this.storage.set(Constants.ADDRESS_IS_FROM_GPS, fromGPS)
        this.apptCreator.address = val.data.address
        this.saveUserAddress(fromGPS);
        this.reset();
        this.refreshAddressField_PullContent();
      }
    })

    await addressModal.present()
  }

  async openDateSelector(event: any) {

    let showAnyTime = !this.apptCreator.mainScreenSelectedServiceTypeList || !this.apptCreator.mainScreenSelectedServiceTypeList.items.length

    const weekSelectorPopover = await this.popoverCtrl.create({
      component: WeekSelectorPage,
      event: event,
      componentProps: {
        timezone: this.apptCreator.apptTime.timezone,
        showAnyTime
      },
      cssClass: ['SCWEB-popover', 'SCWEB-popover-week-selector']
    })

    weekSelectorPopover.onDidDismiss().then((val: any) => {
      if (val && val.data && val.data.weekNumber !== undefined) {
        this.apptCreator.mainScreenSelectedWeek.weekNumber = val.data.weekNumber;
        this.reset()
        this.pullContent();
      }
    })

    await weekSelectorPopover.present()

    // this.datePicker.showMainScreenDateOptions(this.apptCreator.apptTime.timezone, showAnyTime, (weekNumber: number) => {

    //   // this.apptCreator.mainScreenSelectedDay.date = date;
    //   this.apptCreator.mainScreenSelectedWeek.weekNumber = weekNumber;
    //   this.reset()
    //   this.pullContent();
    // })
  }

  async openServices() {

    let servicesModal = await this.modalCtrl.create({
      component: ServicesListPage,
      componentProps: {
        loadFromPage: true,
        proType: this.userService.user.currentProType,
        selectedServiceTypeList: this.apptCreator.mainScreenSelectedServiceTypeList
      },
      cssClass: 'SCWEB-services-modal'
    })

    servicesModal.onDidDismiss().then((val: any) => {

      if (!val || !val.data) {
        return
      }

      const { data } = val
      if (data.newSelectedServiceList) {
        this.apptCreator.mainScreenSelectedServiceTypeList = data.newSelectedServiceList;

        if (this.apptCreator.mainScreenSelectedWeek.weekNumber == -1) {
          // Default to 'This week' if services are selected.
          this.apptCreator.mainScreenSelectedWeek.weekNumber = 0;
        }

      } else {
        let list = new SelectedServiceTypeList()
        list.items = []
        list.refreshText()
        this.apptCreator.mainScreenSelectedServiceTypeList = list
        this.apptCreator.mainScreenSelectedWeek.weekNumber = -1
      }
      this.reset()
      this.pullContent();
    })

    await servicesModal.present();
  }

}
